import React, { useState, useEffect, useMemo, useRef } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// Members Filter Panel — slide-in on mobile, dropdown on desktop.
// Anti-overlay rules:
//   - z-index: 500 panel, z-index: 499 backdrop
//   - height: calc(100vh - 64px) so the bottom nav is never covered
//   - internal overflow-y: auto for long filter lists
// ============================================================

const STATUS_OPTIONS = ["Active", "Inactive", "Visitor", "Left", "Deceased"];
const MARITAL_OPTIONS = ["Married", "Single", "Widowed", "Divorced"];
const SPIRITUAL_OPTIONS = ["Baptized", "Not Yet Baptized", "Ordained"];
const FAMILY_OPTIONS = ["Has Family Record", "No Family Record"];
const FOLLOWUP_OPTIONS = ["Assigned", "Unassigned"];
const BIRTHDAY_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const GENDER_OPTIONS = ["All", "Male", "Female"];

// Map UI status labels to the data values stored on member.memberStatus
// (legacy: Active, New Member, Guest, Inactive, Deceased, Left Church)
export function memberMatchesStatus(member, label) {
  const s = (member.memberStatus || "").toLowerCase();
  const tags = (member.tags || []).map(t => t.toLowerCase());
  switch (label) {
    case "Active":   return s === "active";
    case "Inactive": return s === "inactive";
    case "Visitor":  return s === "guest" || s === "visitor" || tags.includes("visitor");
    case "Left":     return s === "left church" || s === "left";
    case "Deceased": return s === "deceased";
    default: return false;
  }
}

export function memberMatchesSpiritual(member, label) {
  switch (label) {
    case "Baptized": return member.baptized === true;
    case "Not Yet Baptized": return member.baptized !== true;
    case "Ordained": return (member.tags || []).map(t => t.toLowerCase()).includes("ordained");
    default: return false;
  }
}

export function memberMatchesFamily(member, label, families) {
  if (label === "Has Family Record") {
    return Boolean(member.familyId) && (families || []).some(f => f.id === member.familyId);
  }
  if (label === "No Family Record") {
    return !member.familyId || !(families || []).some(f => f.id === member.familyId);
  }
  return true;
}

export function memberMatchesFollowUp(member, label, users) {
  const assigned = (users || []).some(u =>
    Array.isArray(u.assignedMembers) &&
    u.assignedMembers.includes(member.id) &&
    (u.role === "team_member" || u.role === "pastor")
  );
  if (label === "Assigned") return assigned;
  if (label === "Unassigned") return !assigned;
  return true;
}

export function memberMatchesBirthdayMonth(member, monthNames) {
  if (!member.dob) return false;
  // Strip the year and read the raw month from the date string to avoid
  // any timezone shift on YYYY-MM-DD values.
  const m = String(member.dob).match(/^\d{4}-(\d{2})/);
  if (!m) {
    const d = new Date(member.dob);
    if (isNaN(d.getTime())) return false;
    return monthNames.some(name => BIRTHDAY_MONTHS.indexOf(name) === d.getMonth());
  }
  const monthIdx = Number(m[1]) - 1;
  return monthNames.some(name => BIRTHDAY_MONTHS.indexOf(name) === monthIdx);
}

// ============================================================
function Section({ title, hasActive, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", padding: "12px 0" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", background: "transparent", border: "none", cursor: "pointer",
          padding: "6px 2px", display: "flex", alignItems: "center", justifyContent: "space-between",
          fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.10em",
          color: "var(--kc-accent)", textTransform: "uppercase",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {title}
          {hasActive && (
            <span data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, "-")}-dot`}
              style={{ width: 8, height: 8, borderRadius: 999, background: "var(--kc-accent)", display: "inline-block" }} />
          )}
        </span>
        <span style={{ fontSize: 14, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>
      {open && <div style={{ paddingTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>{children}</div>}
    </div>
  );
}

function PillButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: "6px 12px", borderRadius: 999, cursor: "pointer",
        fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
        letterSpacing: "0.04em", transition: "all 0.15s",
        background: active ? "var(--kc-accent)" : "transparent",
        color: active ? "var(--kc-on-accent)" : "var(--kc-sub)",
        border: active ? "1.5px solid var(--kc-accent)" : "1.5px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
      }}
    >
      {children}
    </button>
  );
}

// ============================================================
// Main filter panel.
// `filters` is the unified state object owned by MembersPage.
// ============================================================
export function MembersFilterPanel({
  open, onClose,
  filters, setFilters,
  availableLanguages, availableDepartments,
  isAdmin,
}) {
  const ref = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Toggle helpers
  const toggleArr = (key, val) => setFilters(f => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
  });
  const setVal = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const clearAll = () => setFilters({
    gender: "All",
    statuses: [],
    marital: [],
    languages: [],
    departments: [],
    spiritual: [],
    family: null,
    followup: null,
    birthdayMonths: [],
  });

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          data-testid="filter-backdrop"
          onClick={onClose}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 499,
            animation: "kc-fade-in 0.15s ease-out",
          }}
        />
      )}

      {/* Panel — slide-in from right */}
      <div
        ref={ref}
        data-testid="filter-panel"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0, right: 0,
          width: "min(100%, 380px)",
          // Anti-overlay: stop above 64px bottom nav on mobile.
          height: "calc(100vh - 64px)",
          background: "var(--kc-elevated)",
          boxShadow: "-12px 0 30px rgba(0,0,0,0.55)",
          zIndex: 500,
          overflowY: "auto",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.22,0.61,0.36,1)",
          fontFamily: "'Montserrat',sans-serif",
          color: "#fff",
        }}
      >
        <div style={{ padding: "18px 20px 6px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", position: "sticky", top: 0, background: "var(--kc-elevated)", zIndex: 1 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 14, letterSpacing: "0.14em", color: "var(--kc-accent)", textTransform: "uppercase" }}>
            Filter Members
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="filter-clear-all"
              type="button"
              onClick={clearAll}
              style={{ background: "transparent", color: "var(--kc-accent)", border: "none", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}
            >
              Clear All
            </button>
            <button
              data-testid="filter-close"
              type="button"
              onClick={onClose}
              style={{ background: "transparent", border: "none", color: "var(--kc-text)", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
              aria-label="Close filters"
            >×</button>
          </div>
        </div>

        <div style={{ padding: "8px 20px 80px" }}>
          {/* GENDER */}
          <Section title="Gender" defaultOpen hasActive={filters.gender && filters.gender !== "All"}>
            {GENDER_OPTIONS.map(g => (
              <PillButton key={g} testId={`filter-gender-${g.toLowerCase()}`} active={filters.gender === g} onClick={() => setVal("gender", g)}>{g}</PillButton>
            ))}
          </Section>

          {/* STATUS */}
          <Section title="Status" hasActive={filters.statuses.length > 0}>
            {STATUS_OPTIONS.map(s => (
              <PillButton key={s} testId={`filter-status-${s.toLowerCase()}`} active={filters.statuses.includes(s)} onClick={() => toggleArr("statuses", s)}>{s}</PillButton>
            ))}
          </Section>

          {/* MARITAL */}
          <Section title="Marital Status" hasActive={filters.marital.length > 0}>
            {MARITAL_OPTIONS.map(m => (
              <PillButton key={m} testId={`filter-marital-${m.toLowerCase()}`} active={filters.marital.includes(m)} onClick={() => toggleArr("marital", m)}>{m}</PillButton>
            ))}
          </Section>

          {/* LANGUAGE */}
          <Section title="Language" hasActive={filters.languages.length > 0}>
            {availableLanguages.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--kc-meta)", fontStyle: "italic", padding: "4px 2px" }}>
                No languages found in member records yet.
              </div>
            )}
            {availableLanguages.map(l => (
              <PillButton key={l} testId={`filter-lang-${l.toLowerCase().replace(/\s+/g, "-")}`} active={filters.languages.includes(l)} onClick={() => toggleArr("languages", l)}>{l}</PillButton>
            ))}
          </Section>

          {/* DEPARTMENT */}
          <Section title="Department" hasActive={filters.departments.length > 0}>
            {availableDepartments.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--kc-meta)", fontStyle: "italic", padding: "4px 2px" }}>
                No departments defined yet.
              </div>
            )}
            {availableDepartments.map(d => (
              <PillButton key={d} testId={`filter-dept-${d.toLowerCase().replace(/\s+/g, "-")}`} active={filters.departments.includes(d)} onClick={() => toggleArr("departments", d)}>{d}</PillButton>
            ))}
          </Section>

          {/* SPIRITUAL */}
          <Section title="Spiritual Status" hasActive={filters.spiritual.length > 0}>
            {SPIRITUAL_OPTIONS.map(s => (
              <PillButton key={s} testId={`filter-spiritual-${s.toLowerCase().replace(/\s+/g, "-")}`} active={filters.spiritual.includes(s)} onClick={() => toggleArr("spiritual", s)}>{s}</PillButton>
            ))}
          </Section>

          {/* FAMILY */}
          <Section title="Family" hasActive={filters.family != null}>
            {FAMILY_OPTIONS.map(f => (
              <PillButton key={f} testId={`filter-family-${f.toLowerCase().replace(/\s+/g, "-")}`} active={filters.family === f} onClick={() => setVal("family", filters.family === f ? null : f)}>{f}</PillButton>
            ))}
          </Section>

          {/* FOLLOW-UP — admins only */}
          {isAdmin && (
            <Section title="Follow-Up Assignment" hasActive={filters.followup != null}>
              {FOLLOWUP_OPTIONS.map(f => (
                <PillButton key={f} testId={`filter-followup-${f.toLowerCase()}`} active={filters.followup === f} onClick={() => setVal("followup", filters.followup === f ? null : f)}>{f}</PillButton>
              ))}
            </Section>
          )}

          {/* BIRTHDAY MONTH */}
          <Section title="Birthday Month" hasActive={filters.birthdayMonths.length > 0}>
            {BIRTHDAY_MONTHS.map(m => (
              <PillButton key={m} testId={`filter-bday-${m.toLowerCase()}`} active={filters.birthdayMonths.includes(m)} onClick={() => toggleArr("birthdayMonths", m)}>{m.slice(0, 3)}</PillButton>
            ))}
          </Section>
        </div>
      </div>
      <style>{`
        @keyframes kc-fade-in { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
}

// ============================================================
// Sort dropdown — z-index 400, closes on outside click.
// ============================================================
export const SORT_OPTIONS = [
  { id: "name-asc",  label: "Name A to Z" },
  { id: "name-desc", label: "Name Z to A" },
  { id: "joined-desc", label: "Most Recently Joined" },
  { id: "joined-asc",  label: "Oldest Member" },
  { id: "birthday",  label: "Birthday Soonest" },
  { id: "department", label: "Department" },
];

export function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const current = SORT_OPTIONS.find(s => s.id === value) || SORT_OPTIONS[0];
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid="sort-button"
        className="btn btn-outline"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", padding: "7px 12px" }}
      >
        ⇅ Sort
      </button>
      {open && (
        <div
          data-testid="sort-dropdown"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 6px)",
            background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
            borderRadius: 8, minWidth: 220, padding: "6px 0",
            zIndex: 400, boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
          }}
        >
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.id}
              data-testid={`sort-option-${opt.id}`}
              onClick={() => { onChange(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "10px 14px", background: "transparent", border: "none",
                color: opt.id === current.id ? "var(--kc-accent)" : "var(--kc-sub)",
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600,
                letterSpacing: "0.04em", textAlign: "left", cursor: "pointer",
              }}
            >
              <span>{opt.label}</span>
              {opt.id === current.id && <span style={{ color: "var(--kc-accent)", display: "inline-flex" }}><CustomIcon name="check" size={14} /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Active filter summary tags — normal block flow, dismissible.
// ============================================================
export function ActiveFilterSummary({ filters, setFilters, isAdmin }) {
  const tags = useMemo(() => {
    const t = [];
    if (filters.gender && filters.gender !== "All") {
      t.push({ key: "gender", label: `Gender: ${filters.gender}`, clear: () => setFilters(f => ({ ...f, gender: "All" })) });
    }
    filters.statuses.forEach(v => t.push({ key: `status-${v}`, label: `Status: ${v}`, clear: () => setFilters(f => ({ ...f, statuses: f.statuses.filter(x => x !== v) })) }));
    filters.marital.forEach(v => t.push({ key: `marital-${v}`, label: `Marital: ${v}`, clear: () => setFilters(f => ({ ...f, marital: f.marital.filter(x => x !== v) })) }));
    filters.languages.forEach(v => t.push({ key: `lang-${v}`, label: `Language: ${v}`, clear: () => setFilters(f => ({ ...f, languages: f.languages.filter(x => x !== v) })) }));
    filters.departments.forEach(v => t.push({ key: `dept-${v}`, label: `Dept: ${v}`, clear: () => setFilters(f => ({ ...f, departments: f.departments.filter(x => x !== v) })) }));
    filters.spiritual.forEach(v => t.push({ key: `spi-${v}`, label: `Spiritual: ${v}`, clear: () => setFilters(f => ({ ...f, spiritual: f.spiritual.filter(x => x !== v) })) }));
    if (filters.family) {
      t.push({ key: "family", label: `Family: ${filters.family}`, clear: () => setFilters(f => ({ ...f, family: null })) });
    }
    if (isAdmin && filters.followup) {
      t.push({ key: "followup", label: `Follow-up: ${filters.followup}`, clear: () => setFilters(f => ({ ...f, followup: null })) });
    }
    filters.birthdayMonths.forEach(v => t.push({ key: `bday-${v}`, label: `Bday: ${v.slice(0, 3)}`, clear: () => setFilters(f => ({ ...f, birthdayMonths: f.birthdayMonths.filter(x => x !== v) })) }));
    return t;
  }, [filters, setFilters, isAdmin]);

  if (tags.length === 0) return null;
  return (
    <div data-testid="active-filter-summary" style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 0 4px" }}>
      {tags.map(tag => (
        <span
          key={tag.key}
          data-testid={`active-tag-${tag.key}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 6px 4px 10px", borderRadius: 999,
            background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent)",
            color: "var(--kc-accent)", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
          }}
        >
          {tag.label}
          <button
            type="button"
            onClick={tag.clear}
            style={{ background: "transparent", border: "none", color: "var(--kc-accent)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 4px" }}
            aria-label={`Remove ${tag.label}`}
          >×</button>
        </span>
      ))}
    </div>
  );
}
